import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { ArrowLeft, Sparkle, CaretUp, CaretDown } from "@phosphor-icons/react";
import TrendBadge from "@/components/TrendBadge";
import PriceChart from "@/components/PriceChart";
import AICommentary from "@/components/AICommentary";

export default function TickerDetail() {
  const { assetType, symbol } = useParams();
  const [range, setRange] = useState(180);

  const { data, isLoading } = useSWR(
    ["analysis", assetType, symbol, range],
    () => api.analysis(assetType, symbol, range),
    { refreshInterval: 30000, revalidateOnFocus: false }
  );

  const quote = data?.quote;
  const tech = data?.technical;
  const bars = data?.bars || [];
  const up = (quote?.change_pct ?? 0) >= 0;

  return (
    <div className="px-8 py-6" data-testid="ticker-detail">
      <Link
        to="/"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
      >
        <ArrowLeft size={12} weight="bold" /> Dashboard
      </Link>

      <div className="mt-4 flex items-end justify-between border-b border-zinc-800 pb-6">
        <div>
          <div className="overline">
            {assetType} · {symbol}
          </div>
          <h1 className="font-display text-5xl font-black tracking-tighter mt-1">
            {quote?.name || symbol}
          </h1>
          <div className="mt-2 flex items-center gap-3">
            <span className="tabular text-3xl text-white">
              {quote ? `$${Number(quote.price).toLocaleString(undefined, { maximumFractionDigits: 4 })}` : "—"}
            </span>
            {quote && (
              <span
                className={`tabular text-lg flex items-center gap-1 ${
                  up ? "text-[#00FF66]" : "text-[#FF3B30]"
                }`}
              >
                {up ? <CaretUp weight="bold" /> : <CaretDown weight="bold" />}
                {Math.abs(quote.change_pct).toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <TrendBadge trend={tech?.trend} confidence={tech?.confidence} />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            technical read
          </div>
        </div>
      </div>

      {/* Range selector */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex border border-zinc-800">
          {[
            { l: "7D", v: 7 },
            { l: "1M", v: 30 },
            { l: "3M", v: 90 },
            { l: "6M", v: 180 },
            { l: "1Y", v: 365 },
          ].map((r) => (
            <button
              key={r.v}
              data-testid={`range-${r.l}`}
              onClick={() => setRange(r.v)}
              className={`px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold border-r border-zinc-800 last:border-r-0 transition-colors ${
                range === r.v ? "bg-white text-black" : "text-zinc-500 hover:text-white"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4 border border-zinc-800 p-4 bg-[#0a0a0a]">
        {isLoading ? (
          <div className="h-72 flex items-center justify-center text-zinc-500 text-xs">
            Loading chart…
          </div>
        ) : (
          <PriceChart bars={bars} positive={up} tech={tech} />
        )}
      </div>

      {/* Indicators grid + AI */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-0 border border-zinc-800">
        <div className="p-6 border-b lg:border-b-0 lg:border-r border-zinc-800 bg-[#0a0a0a]">
          <div className="overline mb-3">Technical indicators</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Indicator label="SMA 20" value={tech?.indicators?.sma20} />
            <Indicator label="SMA 50" value={tech?.indicators?.sma50} />
            <Indicator label="SMA 200" value={tech?.indicators?.sma200} />
            <Indicator label="RSI (14)" value={tech?.indicators?.rsi14} suffix="" />
            <Indicator label="MACD" value={tech?.indicators?.macd} />
            <Indicator label="MACD signal" value={tech?.indicators?.macd_signal} />
          </div>
          <div className="overline mt-6 mb-2">Read</div>
          <ul className="space-y-1.5">
            {(tech?.reasons || []).map((r, i) => (
              <li key={i} className="text-xs text-zinc-400 flex items-start gap-2">
                <span className="text-zinc-700">›</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-6 bg-[#0a0a0a]">
          <div className="flex items-center gap-2 overline mb-3">
            <Sparkle size={12} weight="fill" className="text-[#FFD600]" />
            AI Commentary · Claude Sonnet 5
          </div>
          <AICommentary assetType={assetType} symbol={symbol} range={range} />
        </div>
      </div>
    </div>
  );
}

function Indicator({ label, value, suffix = "" }) {
  return (
    <div className="border-l border-zinc-800 pl-3">
      <div className="overline text-[9px]">{label}</div>
      <div className="tabular text-white text-lg mt-0.5">
        {value != null ? `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 })}${suffix}` : "—"}
      </div>
    </div>
  );
}
